import type { CollectiveTenant } from './types';

// Tenant roster for 501 Alliance Avenue, Toronto (a Strashin Developments property).
// Researched from the building rent roll + public sources, June 2026. Financial/lease-cost
// data is intentionally excluded. Source notes live in project-ops/brain/tenants/501-alliance/.
// Note: "Dynamic Fuel Systems" (the legacy name of dynaCERT) is intentionally not listed here
// to avoid a duplicate company card; see its profile note in the brain folder.
export const COLLECTIVE_TENANTS: readonly CollectiveTenant[] = [
  {
    slug: 'flash-forest',
    name: 'Flash Forest',
    categoryId: 'climatetech',
    tagline: 'Drone-powered reforestation and AI forest intelligence.',
    suite: 'Suite 403',
    floor: 'Fourth Floor',
    appointmentOnly: true,
    email: 'info@flashforest.ca',
    hours: 'By appointment',
    established: 'In the building since 2025',
    motif: 'botanical',
    accent: 'forest',
    featured: true,
    story: [
      'Flash Forest is a Canadian reforestation company that plants trees from the air. Founded in 2019 by brothers Bryce and Cameron Jones with Angelique Ahlström, it grew out of a simple question — could land be replanted faster than wildfires and clear-cutting take it away — answered with a fleet of drones, proprietary seed pods, and a great deal of forestry know-how.',
      'The work pairs hardware with intelligence. Drones drop patented seed pods carrying seeds, nutrients, and beneficial fungi, while aerial mapping, LiDAR, and machine learning build a detailed picture of each site before and after planting. To date the company reports more than 2.9 million trees planted across 50-plus projects, partnering with government agencies, forestry firms, and Indigenous communities.',
      'From the fourth floor at 501 Alliance, Flash Forest runs an operation that reaches into burned ridgelines and remote cut blocks conventional crews struggle to access — quiet, technical, patient work that measures success in decades of canopy rather than quarters.',
    ],
    specialties: [
      'Automated drone-based tree planting',
      'Engineered seed pods (seeds, nutrients, mycorrhizal fungi)',
      'Forest Intelligence — drone & LiDAR surveys with AI analysis',
      'Multi-modal reforestation matched to terrain',
      'Large-scale restoration partnerships',
    ],
    website: 'flashforest.ca',
    hero: {
      src: 'https://cdn.prod.website-files.com/67d006c22ab4ba62f9fcd5ad/69959f6439e3e8e4c9f8a46a_FIS_Image_02%281%29.png',
      alt: 'Aerial forest-intelligence imagery from Flash Forest',
      credit: 'flashforest.ca',
    },
    crossLinks: [
      {
        slug: 'cert-systems',
        hook: 'Two climate-tech neighbours rewriting the carbon equation — one captures CO2, the other rebuilds forests.',
      },
      {
        slug: 'dynacert',
        hook: 'A fellow clean-tech tenant working to cut emissions, paired with one putting carbon-capturing forests back in the ground.',
      },
    ],
  },
  {
    slug: 'cert-systems',
    name: 'CERT Systems',
    categoryId: 'climatetech',
    tagline: 'Turning CO2, water and clean power into essential molecules.',
    suite: 'Suite 406',
    floor: 'Fourth Floor',
    appointmentOnly: true,
    email: 'info@co2cert.com',
    hours: 'By appointment',
    established: 'In the building since 2022',
    motif: 'movement',
    accent: 'forest',
    featured: true,
    story: [
      'CERT Systems is a carbontech company on a deceptively simple mission: make the molecules the world runs on without fossil fuels. Using a single-step electrolysis process, CERT combines captured CO2, water, and renewable electricity to produce ethylene — the building block behind countless plastics, chemicals, and fuels — turning a waste emission into a high-value feedstock.',
      'The company grew out of research in the University of Toronto’s Faculty of Applied Science & Engineering before spinning out around 2019. Co-founders Alex Ip (CEO) and Christine Gabardo (CTO) led the team to become the first in the world to convert industrial emissions into ethylene as finalists in the $20M NRG COSIA Carbon XPRIZE, scaling their reactor more than ten-thousand-fold.',
      'From its home on the fourth floor of 501 Alliance, CERT has been named to the Bill Gates–founded Breakthrough Energy Fellows program and validated by organizations including Third Derivative and the XPRIZE Foundation. Every tonne of ethylene the process makes sequesters more than three tonnes of CO2.',
    ],
    specialties: [
      'Direct CO2 electrolysis (CO2 to ethylene)',
      'Carbon capture, utilization and conversion (CCU)',
      'Sustainable eChemicals',
      'Drop-in eMaterials for packaging and textiles',
      'eFuels — sustainable aviation fuel and renewable diesel',
    ],
    website: 'co2cert.com',
    hero: {
      src: 'https://www.utoronto.ca/sites/default/files/CERT-001---8537-crop.jpg',
      alt: 'CERT Systems co-founder Christine Gabardo with the company’s CO2 electrolysis technology',
      credit: 'Schatzypants Inc / University of Toronto',
    },
    crossLinks: [
      {
        slug: 'flash-forest',
        hook: 'One draws CO2 out of the sky into trees; the other turns it into clean chemicals.',
      },
      {
        slug: 'dynacert',
        hook: 'Floor-mates in the building’s clean-tech cluster, chasing lower-carbon industry from opposite ends.',
      },
    ],
  },
  {
    slug: 'dynacert',
    name: 'dynaCERT',
    categoryId: 'climatetech',
    tagline: 'Hydrogen-on-demand tech that cuts diesel emissions and fuel use.',
    suite: 'Suite 105',
    floor: 'Ground Floor',
    appointmentOnly: true,
    email: 'info@dynacert.com',
    phone: '+1-416-766-9691',
    hours: 'Mon–Fri, by appointment',
    established: 'Head office in the building',
    motif: 'mark',
    accent: 'forest',
    featured: true,
    story: [
      'dynaCERT is a publicly traded clean-technology company headquartered at 501 Alliance, where it runs research, development and assembly alongside its corporate offices. Incorporated in 2004 (with roots in the company formerly known as Dynamic Fuel Systems), it designs and manufactures HydraGEN — a hydrogen-on-demand system that uses electrolysis to feed small, measured amounts of hydrogen and oxygen into diesel engines, improving combustion to cut fuel use and emissions.',
      'The HydraGEN line spans engine sizes from compact units to large industrial and marine power plants, across trucking, construction, mining, and power generation. Each installation pairs with HydraLytica telematics, which reports real-time fuel savings and emissions reductions — the measurement layer behind dynaCERT’s carbon-credit program, whose methodology received final Verra approval in October 2024.',
      'The Toronto company trades on the TSX (DYA) and in the U.S. and Frankfurt, with technology deployed in fleets across dozens of countries and a European office in Munich. Long led by founder and Chairman Jim Payne, it announced a 2026 leadership transition as it sharpens its focus on commercial deployment.',
    ],
    specialties: [
      'HydraGEN hydrogen-on-demand emissions-reduction systems',
      'HydraLytica telematics for fuel-savings & GHG reporting',
      'Verra-certified carbon-credit methodology',
      'In-house clean-tech R&D, engineering and assembly',
      'Decarbonization for trucking, construction, mining and marine',
    ],
    website: 'dynacert.com',
    hero: {
      src: 'https://fs.go.iopw.com/fileserver/pageImages/202208/page-826-hydragen-hg1r-front--small---16sep21-Ck8Iv1.png',
      alt: 'dynaCERT HydraGEN HG1R hydrogen-on-demand emissions unit',
      credit: 'dynacert.com',
    },
    crossLinks: [
      {
        slug: 'cert-systems',
        hook: 'Climate-tech kin in the building — different products, shared mission of squeezing carbon out of heavy industry.',
      },
      {
        slug: 'v2r-group',
        hook: 'Same founder, same hallway — Jim Payne also runs builder V2R Group, which fit out spaces here.',
      },
    ],
  },
  {
    slug: 'hollis-morris',
    name: 'Hollis+Morris',
    categoryId: 'makers',
    tagline: 'Handmade lighting and furniture, designed and built in Toronto.',
    suite: 'Suite 4',
    floor: 'Ground Floor',
    appointmentOnly: false,
    email: 'info@hollis-morris.com',
    phone: '+1-647-970-9716',
    publicHours: true,
    hours: 'Showroom · Mon–Fri 9–6; Sat by appointment',
    established: 'In the building since 2022',
    motif: 'lumiere',
    accent: 'brass',
    featured: true,
    story: [
      'Hollis+Morris is a Toronto design and manufacturing studio making contemporary lighting and furniture by hand. It was founded by Mischa Couvrette, who came to design by an unusual route — a degree in marine biology, then restoring a steel sailboat and sailing it from Halifax to Guatemala, before studying furniture at Sheridan College. The brand debuted in 2015 at ICFF in New York, with an environmental mission from the start.',
      'Everything is designed, prototyped, and built in-house at 501 Alliance. In 2024, marking its tenth year, the studio expanded into a roughly 14,000-square-foot facility with distinct wood, metal, finishing, and assembly zones — the production floor visible from a daylit, public showroom. As Couvrette puts it, “it’s quite literally being put together in the same space that you’re getting to experience.”',
      'Pieces span pendants, sconces, and floor and table lamps alongside tables, seating, casegoods, and a newer upholstered collection, in Canadian hardwoods and southern-Ontario metalwork. A certified B Corp, the studio uses FSC-certified wood and now takes on contract and commercial work, with designs carried by retailers including Lumens and 2Modern.',
    ],
    specialties: [
      'Handmade contemporary lighting',
      'Solid-wood furniture and casegoods',
      'In-house design, prototyping and fabrication',
      'Sustainable local materials (certified B Corp)',
      'Custom and contract / commercial projects',
    ],
    website: 'hollisandmorris.com',
    hero: {
      src: 'https://hollisandmorris.com/cdn/shop/files/hollis-morr-s3daysofdesign-three_copy_2.jpg?v=1781727635&width=1600',
      alt: 'A Hollis+Morris lighting installation exhibited at 3daysofdesign',
      credit: 'hollisandmorris.com',
    },
    crossLinks: [
      {
        slug: 'box-design',
        hook: 'A family of makers under one roof — Box Design’s co-owner Mischa Couvrette also founded Hollis+Morris.',
      },
      {
        slug: 'luxor-by-design',
        hook: 'Two makers working in warm natural materials — lighting and furniture here, solid-wood cabinetry there.',
      },
    ],
  },
  {
    slug: 'a-plus-creative',
    name: 'A Plus Creative',
    categoryId: 'studios',
    tagline: 'A 40,000 sq ft daylight studio for retail & e-commerce content.',
    suite: 'Suite 407',
    floor: 'Fourth Floor',
    appointmentOnly: true,
    email: 'hello@apluscreative.ca',
    phone: '+1-416-537-9999',
    hours: 'By appointment',
    established: 'In the building since 2017',
    motif: 'frame',
    accent: 'brass',
    featured: true,
    story: [
      'A Plus Creative is a full-service content and e-commerce production studio, and the largest tenant at 501 Alliance. Founded in 2012 by Anastasia Adani (CEO) and Alex Adani (COO), it spreads a 40,000-square-foot production centre across Suites 407 and 404 on the fourth floor, serving marketing, creative, and e-commerce leaders from brand strategy to finished campaign assets.',
      'The studio is built for volume and versatility, with nine multifunctional daylight studios, a virtual production stage, and an in-house carpentry and set-building department — the workshop that explains its outsized footprint. Work spans photography, video, retouching, 3D/CGI, copywriting, and casting for fashion, beauty, home, health, and self-care retail.',
      'A Plus produces content for many of North America’s best-known retailers and brands, from Loblaws and Hudson’s Bay to Estée Lauder, Clinique, The Ordinary, Macy’s, and Bloomingdale’s. It was recognized with Canada’s Awards of Excellence in 2021 and 2022, and supports causes including CAMH and SickKids.',
    ],
    specialties: [
      'Retail & e-commerce content production',
      'In-house set building across nine daylight studios',
      'Virtual production and 3D/CGI rendering',
      'Brand strategy, creative campaigns and copywriting',
      'Talent casting and turnkey e-commerce pipelines',
    ],
    website: 'apluscreative.ca',
    hero: {
      src: 'https://cdn.prod.website-files.com/621518121ed5d6d8345a1cec/625feb50a73fc53996d42fd7_01%20ESTEE%20LAUDER_shades%20of%20canada_OFFFIG_Shot1.jpg',
      alt: 'An Estée Lauder beauty campaign produced by A Plus Creative',
      credit: 'apluscreative.ca',
    },
    crossLinks: [
      {
        slug: 'starvox-entertainment',
        hook: 'Two production houses — A Plus building content for the camera, Starvox staging spectacle for live audiences.',
      },
      {
        slug: 'david-tomlin',
        hook: 'A working painter and a large production studio, sharing the building’s creative floors.',
      },
    ],
  },
  {
    slug: 'mail-order-mystery',
    name: 'Mail Order Mystery',
    categoryId: 'recreation',
    tagline: 'Immersive mystery adventures that arrive in the mail.',
    suite: 'Suite 307',
    floor: 'Third Floor',
    appointmentOnly: true,
    phone: '+1-647-483-6048',
    hours: 'Online · ships worldwide',
    established: 'In the building since 2024',
    motif: 'bindery',
    accent: 'brass',
    featured: true,
    story: [
      'Mail Order Mystery began in late 2015 at a Toronto kitchen table, when writer and mom Suzanne Matczuk set out to make a gift engaging enough to pull her tweens away from their screens. Her answer arrives the old-fashioned way, through the mailbox: each mystery unfolds over roughly six weekly installments of letters, ciphers, maps, keys and keepsakes, written directly to the child by the characters — so the kid isn’t just reading an adventure, they’re the hero of it.',
      'From Suite 307, the team produces and ships five hand-crafted story experiences worldwide — a pirate caper, a fairy-tale, a spy romp, a detective whodunnit, and a time-travel adventure for the whole family. Most are aimed at the famously hard-to-buy-for 8-to-13 crowd, though younger and older solvers get hooked too.',
      'A decade on, the company is a quiet word-of-mouth favourite among parents and grandparents, with thousands of reviews and a 2026 nod from New York Times Wirecutter, which called it “in a class of its own.” A fitting tenant for a building full of makers: a small studio that still bets on paper, wax seals and a well-timed envelope.',
    ],
    specialties: [
      'Serialized “story-in-the-mail” mystery experiences',
      'Five themed adventures (pirate, fairy-tale, spy, detective, time-travel)',
      'Personalized letters with codes, puzzles and physical clues',
      'Collectible keepsakes that resolve each mystery',
      'Built for kids 8–13, with options for all ages',
    ],
    website: 'mailordermystery.com',
    hero: {
      src: 'https://www.mailordermystery.com/cdn/shop/t/20/assets/home_slide_1.jpg?v=26590866383826360181771367511',
      alt: 'A Mail Order Mystery adventure kit for kids',
      credit: 'mailordermystery.com',
    },
    crossLinks: [
      {
        slug: 'starvox-entertainment',
        hook: 'Two neighbours in the experience business — one stages immersive theatre, the other mails immersive adventures.',
      },
      {
        slug: 'tracer-golf',
        hook: 'Proof the building is a hub for play, whether on the screen or in the mailbox.',
      },
    ],
  },
  {
    slug: 'tracer-golf',
    name: 'Tracer Golf',
    categoryId: 'recreation',
    tagline: 'Canada’s first fully automated indoor golf range, open 24/7.',
    suite: 'Suite 3',
    floor: 'Ground Floor',
    appointmentOnly: true,
    email: 'info@tracergolf.ca',
    phone: '+1-647-812-8240',
    publicHours: true,
    hours: 'Open 24/7 · book online',
    established: 'In the building since 2022',
    motif: 'movement',
    accent: 'forest',
    featured: false,
    story: [
      'Tracer Golf began as a family project. About 30 years ago the founders’ father owned a golf course outside Toronto; for his 80th birthday the family built him a simple simulator room. Watching everyone’s game improve through frequent, data-driven practice, they set out to make the experience affordable and open to all — and in 2019 opened Canada’s first fully automated indoor golf facility.',
      'The Toronto West range at 501 Alliance opened in early 2023 in Suite 3, with eight private, climate-controlled bays built for focused practice: suspended screens, solid sidewalls, 10-foot clearance, automatic ball return, and a launch monitor at every mat showing full ball-flight data. It’s entirely self-serve — guests book online, enter with a smart key in the app, and play any time, day or night.',
      'The company has grown quickly across the GTA, with locations from the Beaches to Mississauga, Newmarket and Whitby, earning a place on the Globe and Mail’s ranking of Canada’s Top Growing Companies — #40 in 2024, rising to #21 in 2025.',
    ],
    specialties: [
      'Indoor driving range with a launch monitor at every bay',
      'Golf simulator play across 250+ courses',
      'Private lessons with certified teachers',
      'Short-game, putting and chipping practice',
      'Fully automated, 24/7 self-serve access via app',
    ],
    website: 'tracergolf.ca',
    hero: {
      src: 'https://tracergolf.ca/wp-content/uploads/driving-range-impact-vision-1.avif',
      alt: 'Inside a Tracer Golf Impact Vision simulator bay',
      credit: 'tracergolf.ca',
    },
    crossLinks: [
      {
        slug: 'alliance-banquet-hall',
        hook: 'Two ways to gather under one roof — book a bay for a round, then celebrate next door.',
      },
      {
        slug: 'mail-order-mystery',
        hook: 'A fellow recreation tenant proving the building is built for play.',
      },
    ],
  },
  {
    slug: 'starvox-entertainment',
    name: 'Starvox Entertainment',
    categoryId: 'studios',
    tagline: 'Toronto producer of touring theatre and immersive exhibitions.',
    suite: 'Suite 225',
    floor: 'Second Floor',
    appointmentOnly: true,
    hours: 'Production office · by appointment',
    established: 'Production office in the building',
    motif: 'lumiere',
    accent: 'brass',
    featured: false,
    story: [
      'Starvox Entertainment is a Toronto-born company producing, touring, and marketing live shows across North America and beyond since 2005. Founded by Corey Ross, it built its name on bold theatrical properties — the cult-favourite “Evil Dead — The Musical,” the West End parody hit “Potted Potter,” the illusion show “Champions of Magic,” and the Canadian production of Andrew Lloyd Webber’s “CATS.”',
      'The company helped spark the immersive-art boom: through its Starvox Exhibits division and a partnership with Show One Productions, it co-created Lighthouse Immersive and the original “Immersive Van Gogh,” which sold more than five million tickets across North America, followed by “The Art of Banksy” and immersive experiences around Frida Kahlo and Disney animation.',
      'From its production office at 501 Alliance, Starvox runs the essential behind-the-scenes side of show business — booking tours, coordinating marketing, and moving productions from city to city.',
    ],
    specialties: [
      'Producing and touring live theatre',
      'Immersive and touring art exhibitions (Starvox Exhibits)',
      'Show marketing and entertainment production',
      'Family, comedy and magic stage productions',
      'Partnerships and co-productions',
    ],
    website: 'starvoxent.com',
    hero: {
      src: 'https://mma.prnewswire.com/media/2367617/Starvox_Exhibits_STARVOX_EXHIBITS_BRINGS_MONA_LISA_TO_TORONTO__.jpg',
      alt: 'The Art of Banksy touring exhibition presented by Starvox Exhibits',
      credit: 'CNW Group / Starvox Exhibits',
    },
    crossLinks: [
      {
        slug: 'a-plus-creative',
        hook: 'A natural neighbour when a show needs content, design, or campaign support.',
      },
      {
        slug: 'mail-order-mystery',
        hook: 'Another tenant in immersive, story-driven experiences — on stage and through the mailbox.',
      },
    ],
  },
  {
    slug: 'box-design',
    name: 'Box Design',
    categoryId: 'makers',
    tagline: 'Modern terrace design-build and custom interiors.',
    suite: 'Suite 213A',
    floor: 'Second Floor',
    appointmentOnly: true,
    email: 'info@boxdesign.ca',
    phone: '+1-416-795-3779',
    hours: 'By appointment · on-site consultation',
    established: 'In the building since 2023',
    motif: 'frame',
    accent: 'brass',
    featured: false,
    story: [
      'Box Design is a Toronto design-build studio devoted to modern outdoor spaces — and, increasingly, the interiors that frame them. Founded in 2003, it transforms condo terraces and backyards into low-maintenance, four-season retreats: insulated planter boxes, Ipe decking, porcelain tile, aluminum pergolas, integrated lighting, and automated irrigation, detailed to weather the Canadian winter.',
      'The company is run by brothers Dylan and Mischa Couvrette, who took ownership in 2021. Mischa is also the founder behind contemporary furniture brand hollis+morris, while Dylan came from operations — a pairing focused on a minimal, modern aesthetic executed with precision.',
      'From the second floor of 501 Alliance, Box Design works project by project, each beginning with an on-site visit to map a client’s wish-list. In recent years it has extended its custom-build sensibility indoors, bringing the same standards to interior millwork and finishes.',
    ],
    specialties: [
      'Condo terrace design-build',
      'Residential backyard landscaping',
      'Custom insulated planter boxes and Ipe decking',
      'Pergolas, porcelain tile, lighting and irrigation',
      'Custom interior builds and millwork',
    ],
    website: 'boxdesign.ca',
    hero: {
      src: 'https://images.squarespace-cdn.com/content/v1/5fa89a02c9eaa2281e999aae/6b4fa4ff-77d8-4879-9c76-113b672b2ba1/BOX_DESIGN_dundas1_luxury_rooftop_pergola_custom_terrace_design_toronto_patio_bar_porcelain_tile_05.jpg',
      alt: 'A custom luxury rooftop terrace designed and built by Box Design',
      credit: 'boxdesign.ca',
    },
    crossLinks: [
      {
        slug: 'hollis-morris',
        hook: 'Co-owner Mischa Couvrette also founded Hollis+Morris — two makers, one family, one building.',
      },
      {
        slug: 'mavericks-design-build',
        hook: 'Kindred design-build neighbours turning client wish-lists into finished spaces.',
      },
    ],
  },
  {
    slug: 'luxor-by-design',
    name: 'Luxor by Design',
    categoryId: 'makers',
    tagline: 'Solid-wood bathroom vanities, designed and built in Toronto.',
    suite: 'Suite 103',
    floor: 'Ground Floor',
    appointmentOnly: true,
    phone: '+1-416-745-8967',
    hours: 'Mon–Fri 11–6:30 · Sat 11–4',
    established: 'In the building since 2021',
    motif: 'lumiere',
    accent: 'brass',
    featured: false,
    story: [
      'Luxor by Design is a Toronto maker of luxury home-and-bath furniture, specializing in solid-wood bathroom vanities crafted from birch, walnut and ebony and finished with mitered-edge quartz countertops. The work leans on traditional cabinetmaking — dovetail joinery, deep drawers, fully-assembled construction — paired with a contemporary look, all designed in Canada under the LUXOR brand.',
      'The studio and showroom sit on the ground floor of 501 Alliance, where the company has been since 2021. Beyond vanities, Luxor carries a broader bath line of faucets, shower panels, mirrors and accessories, and distributes through Ontario kitchen-and-bath dealers. The principal, Areff, is the contact customers meet in the showroom.',
      'The showroom runs largely by appointment, and customers describe walking in for one vanity and leaving with a plan for the whole house. As a small, owner-led fabricator it keeps a quieter footprint than its larger neighbours, but its collections show a consistent house style: warm solid woods, clean lines and stone tops.',
    ],
    specialties: [
      'Solid-wood bathroom vanities with quartz tops',
      'Traditional cabinetmaking (dovetail joinery)',
      'In-house designed LUXOR collections',
      'Coordinated bath fixtures and accessories',
      'Made-in-Canada custom sizing',
    ],
    website: 'luxorbydesigns.com',
    crossLinks: [
      {
        slug: 'hollis-morris',
        hook: 'Two makers working warm natural materials — lighting and furniture, and solid-wood cabinetry.',
      },
      {
        slug: 'box-design',
        hook: 'A natural pairing for a renovation: terrace design-build and made-to-order vanities.',
      },
    ],
  },
  {
    slug: 'david-tomlin',
    name: 'David Tomlin',
    categoryId: 'studios',
    tagline: 'Canadian painter of surreal, light-filled landscapes.',
    suite: 'Suite 210',
    floor: 'Second Floor',
    appointmentOnly: true,
    email: 'info@davidtomlin.ca',
    phone: '+1-416-418-2496',
    hours: 'Studio visits by appointment',
    established: 'In the building since 2011',
    motif: 'frame',
    accent: 'brass',
    featured: false,
    story: [
      'David Tomlin is a Toronto painter who has kept a studio at 501 Alliance since around 2011, working from Suite 210. A former Ontario elementary-school principal who studied at York University and OISE, he turned to painting full-time after retirement and earned a graduate certificate in Independent Studio Practice from the Haliburton School of Art + Design.',
      'His work renders a distinctly Canadian sense of place: surreal, vibrant landscapes built from abstract shapes and luminous colour, often developed from his own on-location photographs. He cites Impressionism and the work of Canadian First Nations artists among his influences, describing each canvas as an attempt to hold together order and chaos.',
      'Tomlin has shown widely, including The Artist Project in Toronto, Spectrum Miami, and the Clio Art Fair in New York, with paintings in private collections across Canada, the U.S., and Europe. He is a member of The 501 Alliance for Art and the Toronto West Art Collective.',
    ],
    specialties: [
      'Surreal, semi-abstract Canadian landscape painting',
      'Vibrant colour and light-driven composition',
      'Original paintings, limited-edition prints and art cards',
      'Studio visits and direct sales by appointment',
    ],
    website: 'davidtomlin.ca',
    hero: {
      src: 'https://d15yhgn2ui21mw.cloudfront.net/production/21354/MDAwMDAwMDAwMDAw7QGraePd6CACehR_ZcfBFYwXu2FyveAYEUnRGwCTQQQLtneRwWETeR6yOMFIOjy24aDow_LJj7F22f2vd2S3k0FN_38EZqodfTc2xEuhDJufAHDziomoKXOdJ3Hs_jG_Da6jI5Eudq_OlWAGPJ5eMn57LHqDbd35Dk2cd05Z89KBRTd8ZOEmqdlUVP-tROoc3Nk5zrtTDEoj-7i6QbqwlTJPg_KxcPPpZTMHDd8i68AaU9q_XnnMqGjDdA.jpg',
      alt: 'An original contemporary painting by Toronto artist David Tomlin',
      credit: 'davidtomlin.ca',
    },
    crossLinks: [
      {
        slug: 'a-plus-creative',
        hook: 'A neighbour in the building’s creative-studios wing, where image-making is the daily work.',
      },
      {
        slug: 'starvox-entertainment',
        hook: 'Fellow member of 501’s studios community, turning the building into a hub for Toronto’s arts.',
      },
    ],
  },
  {
    slug: 'canadian-logistics-institute',
    name: 'Canadian Logistics Institute',
    categoryId: 'services',
    tagline: 'Certifying Canada’s supply-chain professionals since 1990.',
    suite: 'Suite 405A',
    floor: 'Fourth Floor',
    appointmentOnly: true,
    email: 'loginfo@loginstitute.ca',
    hours: 'By appointment · programs online & in person',
    established: 'In the building since 2016',
    motif: 'facade',
    accent: 'brass',
    featured: false,
    story: [
      'Operating as The Logistics Institute, the Canadian Logistics Institute is a Toronto-based, not-for-profit professional body devoted to the people who keep supply chains moving. Founded in 1990 by Victor Deyglio — still its Founding President — it has spent more than three decades turning practical experience into recognized professional standing for logisticians across Canada and beyond.',
      'The Institute describes itself not as a school but as a professional organization. Its programs lead to certification and designations — most notably the P.Log (Professional Logistician) and LS (Logistics Specialist) — earned through a competency-based, leadership-focused approach. Pathways run from entry-level Logistics Essentials to strategic leadership programs, with corporate group training.',
      'From the fourth floor of 501 Alliance, the Institute reaches a global community of certified professionals, supported by a board drawn from companies across Canada and worldwide, with a mission to establish supply chain logistics as a worldwide profession.',
    ],
    specialties: [
      'P.Log (Professional Logistician) certification',
      'LS (Logistics Specialist) certification',
      'Career-staged professional development programs',
      'Corporate / team training',
      'Competency standards and workforce research',
    ],
    website: 'loginstitute.ca',
    crossLinks: [
      {
        slug: 'metropolitan-employment-centre',
        hook: 'Two tenants in the people business — one places talent into work, the other advances careers.',
      },
      {
        slug: 'v2r-group',
        hook: 'Professional-services neighbours, each helping organizations build capability.',
      },
    ],
  },
  {
    slug: 'v2r-group',
    name: 'V2R Group',
    categoryId: 'services',
    tagline: 'Vision 2 Reality — multi-generational GTA general contractors.',
    suite: 'Suite 201',
    floor: 'Second Floor',
    appointmentOnly: true,
    phone: '+1-416-360-0700',
    hours: 'By appointment',
    established: 'In the building since 2012',
    motif: 'mark',
    accent: 'brass',
    featured: false,
    story: [
      'V2R Group is a family-owned general contractor that turns ideas into finished spaces — “Vision 2 Reality.” Founded by Jim Payne, the firm draws on five generations of carpentry and construction know-how, with three generations of the Payne family currently active. Today Jim’s sons, Jesse and Joshua Payne, lead the day-to-day work from Suite 201.',
      'The team specializes in commercial renovations and tenant fit-outs, corporate design-build, and ground-up new builds and expansions for industrial, commercial, and institutional clients across the GTA — managing the full arc from design and engineering through trades and final inspection.',
      'Fittingly, some of that work has happened just down the hall: V2R’s portfolio includes build-outs at 501 Alliance for fellow tenants, a reminder that the building’s own spaces are often crafted by neighbours who know it best.',
    ],
    specialties: [
      'Commercial renovations and tenant fit-outs',
      'Corporate design-build',
      'New builds and facility expansions',
      'Project and construction management',
      'Sustainable / LEED-minded building practices',
    ],
    website: 'v2rgroup.ca',
    hero: {
      src: 'https://v2rgroup.ca/wp-content/uploads/2025/04/v2rhero-scaled.jpg',
      alt: 'A commercial interior built by V2R Group',
      credit: 'v2rgroup.ca',
    },
    crossLinks: [
      {
        slug: 'dynacert',
        hook: 'Same founder, same hallway — V2R’s Jim Payne also chairs climate-tech neighbour dynaCERT.',
      },
      {
        slug: 'hollis-morris',
        hook: 'When the maker studio needed its space shaped, V2R was the builder behind the build.',
      },
    ],
  },
  {
    slug: 'alliance-banquet-hall',
    name: 'Alliance Banquet Hall',
    categoryId: 'recreation',
    tagline: 'Weddings, concerts and celebrations — your story, our stage.',
    suite: 'Suite 1',
    floor: 'Ground Floor',
    appointmentOnly: true,
    email: 'alliancebanquethall@gmail.com',
    phone: '+1-416-766-5620',
    hours: 'By appointment · event bookings',
    established: 'In the building since 1995',
    motif: 'lumiere',
    accent: 'brass',
    featured: false,
    story: [
      'For three decades, the large ground-floor hall at 501 Alliance has been one of the west end’s go-to gathering places. A banquet operation has run here since the 1990s — the address carried earlier hall names before the current operator reopened the room as Alliance Banquet Hall & Event Venue, billed plainly as “your story, our stage.”',
      'The space is built for scale and flexibility: up to 600 guests for a plated banquet and as many as 900 concert-style, with high ceilings, a full bar, on-site parking and in-house catering. The same room can host a wedding one weekend, a quinceañera the next, and a comedy night or corporate function after that.',
      'What the team leans into is cultural fit over formula — menus and details shaped around each family’s traditions, with a kitchen that draws on European, Portuguese and Brazilian cooking. It’s a long-term anchor tenant that has quietly marked thousands of milestones for Toronto’s York and Mount Dennis communities.',
    ],
    specialties: [
      'Weddings and large milestone celebrations',
      'Concert-style and live entertainment events',
      'Corporate functions, galas and private parties',
      'Full in-house custom catering',
      'Flexible large hall with bar and on-site parking',
    ],
    website: 'alliancebanquethall.com',
    hero: {
      src: 'https://alliancebanquethall.com/wp-content/uploads/elementor/thumbs/alliance-05-rcmiu9n9w1yjchlpftc5dseeofuca5jlz064yufds8.jpg',
      alt: 'Alliance Event Venue dressed for a celebration',
      credit: 'alliancebanquethall.com',
    },
    crossLinks: [
      {
        slug: 'starvox-entertainment',
        hook: 'A live-events producer and a 900-capacity hall under one roof — the building’s home for shows.',
      },
      {
        slug: 'mail-order-mystery',
        hook: 'Two neighbours in the business of staging memorable moments.',
      },
    ],
  },
  {
    slug: 'unemit',
    name: 'unemit',
    categoryId: 'ventures',
    tagline: 'Direct air capture, built to scale carbon removal to gigatons.',
    suite: 'Suite 221',
    floor: 'Second Floor',
    appointmentOnly: true,
    email: 'contact@unemit.com',
    hours: 'By appointment',
    established: 'In the building since 2024',
    motif: 'mark',
    accent: 'forest',
    featured: false,
    story: [
      'unemit is a carbon-removal company on a mission to pull carbon dioxide back out of the air — its name a quiet promise to “un-emit” what has already been released. Founded by Christian Haakonsen, it develops Direct Air Capture (DAC) technology designed to be scalable, swift and sustainable: CO2 is drawn in through modular air contactors, then released from a sorbent using low-temperature renewable heat for permanent storage or industrial use.',
      'The founder’s path is unusual: degrees in math, physics and astrophysics at McGill, a master’s and PhD in Nuclear Science and Engineering at MIT, and a stint at Boston Consulting Group. He sums up the goal as “60-by-’60” — capturing at least 60 gigatons of CO2 by 2060. The work has been recognized by Breakthrough Energy, which named Haakonsen an Innovator Fellow.',
      'Toronto-based and research-driven, unemit took a second-floor suite at 501 Alliance in 2024, joining a small cluster of climate and clean-energy ventures in the building.',
    ],
    specialties: [
      'Direct Air Capture (DAC) technology development',
      'Permanent carbon dioxide removal',
      'Modular, factory-assembled air contactors',
      'Low-temperature sorbent regeneration',
      'Gigaton-scale carbon-removal engineering',
    ],
    website: 'unemit.com',
    hero: {
      src: 'https://unemit.com/wp-content/uploads/2022/07/much-to-protect.jpg',
      alt: 'Unemit — “because we have so much we must protect”',
      credit: 'unemit.com',
    },
    crossLinks: [
      {
        slug: 'cert-systems',
        hook: 'Where unemit pulls CO2 from the air, CERT Systems turns captured CO2 into useful chemicals.',
      },
      {
        slug: 'flash-forest',
        hook: 'Two takes on drawing down carbon — capture from the air, and forests planted by drone.',
      },
    ],
  },
  {
    slug: 'polytex',
    name: 'Polytex',
    categoryId: 'makers',
    tagline: 'A design-and-fabrication tenant at 501 Alliance.',
    suite: 'Suite 226',
    floor: 'Second Floor',
    appointmentOnly: true,
    hours: 'By appointment',
    established: 'In the building since 2018',
    motif: 'tailor',
    accent: 'brass',
    featured: false,
    story: [
      'Polytex occupies a second-floor suite at 501 Alliance and has been part of the building’s community since 2018, grouped with the makers cohort — the design-and-fabrication shops that turn raw materials into finished product.',
      'A fuller public profile of this particular company is still being confirmed: “Polytex” is shared by many unrelated businesses, so rather than borrow another firm’s story, this entry sticks to what the building can verify. The most likely reading is a textiles- or materials-oriented fabrication business.',
      'This profile will be expanded with details directly from the tenant — what they make, when they were founded, and how to get in touch.',
    ],
    specialties: ['Design & fabrication (details to be confirmed with the tenant)'],
    website: 'polytex.ca',
    crossLinks: [
      {
        slug: 'hollis-morris',
        hook: 'A fellow maker anchoring the building’s design-and-fabrication cohort.',
      },
      {
        slug: 'box-design',
        hook: 'Another in-building maker in the craft-and-fabrication community on Alliance Avenue.',
      },
    ],
  },
  {
    slug: 'mavericks-design-build',
    name: 'Mavericks Design Build',
    categoryId: 'makers',
    tagline: 'A design-build studio in the building’s maker community.',
    suite: 'Suite 100B',
    floor: 'Ground Floor',
    appointmentOnly: true,
    hours: 'By appointment',
    established: 'In the building since 2023',
    motif: 'frame',
    accent: 'brass',
    featured: false,
    story: [
      'Mavericks Design Build is one of the design-and-fabrication tenants on the ground floor of 501 Alliance, sharing a building with a small cluster of Toronto makers — terrace and interiors builder Box Design, lighting-and-furniture studio Hollis+Morris, and others. Its lease here dates to 2023, with day-to-day matters run by its principal.',
      'The studio keeps a deliberately low public profile — reached directly rather than through a storefront, in the rhythm of a project-based design-build practice where work arrives by referral and word of mouth.',
      'This entry is kept brief and factual on purpose, and will be expanded once the studio shares more about its work.',
    ],
    specialties: [
      'Design-build / construction',
      'Custom fabrication and interiors (to be confirmed)',
    ],
    website: '—',
    crossLinks: [
      {
        slug: 'box-design',
        hook: 'Kindred design-build neighbours turning client wish-lists into finished spaces.',
      },
      {
        slug: 'hollis-morris',
        hook: 'Part of the same maker community, where furniture, lighting and built interiors share a roof.',
      },
    ],
  },
  {
    slug: 'metropolitan-employment-centre',
    name: 'Metropolitan Employment Centre',
    categoryId: 'services',
    tagline: 'A long-running west-end employment office.',
    suite: 'Suite 104',
    floor: 'Ground Floor',
    appointmentOnly: true,
    hours: 'By appointment',
    established: 'In the building since 2015',
    motif: 'facade',
    accent: 'brass',
    featured: false,
    story: [
      'Metropolitan Employment Centre has worked from the ground floor of 501 Alliance since 2015, one of the building’s longer-tenured occupants. It operates as an employment and job-placement office in Toronto’s west end — the historically industrial Mount Dennis / Weston corridor, where work, newcomers, and manufacturing have long met.',
      'The Centre keeps a low public profile, contact running through the people who staff it rather than a brand — the pattern of a smaller, relationship-driven employment office that serves a steady stream of returning clients by word of mouth.',
      'Details of its programs and leadership are not publicly documented, so this entry is intentionally brief: a decade-long west-end tenant in the business of helping people find work.',
    ],
    specialties: [
      'Employment / job-placement services',
      'Serving the Mount Dennis / Weston area since 2015',
    ],
    website: '—',
    crossLinks: [
      {
        slug: 'canadian-logistics-institute',
        hook: 'Two tenants in the people business — one places workers, the other certifies careers.',
      },
      {
        slug: 'v2r-group',
        hook: 'Professional-services neighbours helping people and organizations build capability.',
      },
    ],
  },
  {
    slug: 'webhand',
    name: 'WebHand',
    categoryId: 'studios',
    tagline: 'A small web and digital studio in the building’s creative wing.',
    suite: 'Suite 208',
    floor: 'Second Floor',
    appointmentOnly: true,
    hours: 'By appointment',
    established: 'In the building since 2025',
    motif: 'mark',
    accent: 'brass',
    featured: false,
    story: [
      'WebHand is one of 501 Alliance’s smallest and newest tenants — a compact second-floor office on a one-year term beginning in 2025. The name and the building’s creative-studios grouping point toward web or digital work, the kind of small studio or solo practice a compact private office suits well.',
      'Beyond that, the public record is thin, and we’ve deliberately left details blank rather than risk attaching the wrong company to this suite.',
      'If WebHand is your studio, we’d welcome the details — what you build, who you work with, and where to find you — so this profile can grow into a fuller story.',
    ],
    specialties: ['Web / digital studio work (to be confirmed)'],
    website: '—',
    crossLinks: [
      {
        slug: 'a-plus-creative',
        hook: 'Two ends of the creative spectrum — a 40,000 sq ft production studio and a compact digital workspace.',
      },
      {
        slug: 'fetchly',
        hook: 'A neighbour for the building’s digital makers — software and web from different desks.',
      },
    ],
  },
  {
    slug: 'fetchly',
    name: 'Fetchly',
    categoryId: 'ventures',
    tagline: 'An early-stage Toronto software venture.',
    suite: 'Suite 107',
    floor: 'Ground Floor',
    appointmentOnly: true,
    hours: 'By appointment',
    established: 'In the building since 2023',
    motif: 'mark',
    accent: 'brass',
    featured: false,
    story: [
      'Fetchly is one of the building’s smaller software ventures, holding a ground-floor suite at 501 Alliance. Behind the trade name is a Toronto federal corporation, incorporated in 2021 and active today — the kind of lean, founder-run company the building’s ventures tier was set up to give a home and an address.',
      'The venture traces back to founder Brendan Acs, a west-end Torontonian whose name sits on its early ownership records and lease file. Fetchly took its suite in 2023 and has kept a low public profile since.',
      'We’ve kept this profile deliberately spare — “Fetchly” is a common name across tech and retail — and stuck to what the building and public record can confirm. Details to round it out are warmly welcome.',
    ],
    specialties: ['Early-stage software venture', 'Founder-led, ground-floor operation'],
    website: '—',
    crossLinks: [
      {
        slug: 'unemit',
        hook: 'A neighbour in the ventures tier — proof the ground floor is a launchpad as much as a workshop.',
      },
      {
        slug: 'webhand',
        hook: 'Two of the building’s digital makers, sketching the same online world from different desks.',
      },
    ],
  },
  {
    slug: 'formcore',
    name: 'Formcore',
    categoryId: 'makers',
    tagline: 'A design-and-fabrication studio joining 501 Alliance in 2027.',
    suite: 'Suite 305B',
    floor: 'Third Floor',
    appointmentOnly: true,
    hours: 'Opening 2027',
    established: 'Joining in 2027',
    motif: 'frame',
    accent: 'brass',
    featured: false,
    story: [
      'Formcore is an incoming tenant at 501 Alliance, with a lease set to begin in 2027. The building places it in the makers cohort — the design-and-fabrication shops that turn raw materials into finished work — alongside neighbours such as Hollis+Morris, Box Design, and Polytex, in Suite 305B on the third floor.',
      'Beyond the category, little about this particular Formcore is yet confirmed; the name is shared by unrelated companies elsewhere, so this entry leaves those details out rather than attach another company’s story.',
      'As an incoming maker, Formcore joins a building already shaped by craft — studios that design and build, mill and assemble, and finish their own work on site. This profile will be expanded once the tenant is established.',
    ],
    specialties: ['Design and fabrication (specifics to be confirmed)'],
    website: '—',
    crossLinks: [
      {
        slug: 'hollis-morris',
        hook: 'A fellow maker anchoring the design-and-fabrication cohort that Formcore is joining.',
      },
      {
        slug: 'box-design',
        hook: 'Another in-building maker and design-build studio on Alliance Avenue.',
      },
    ],
  },
];

export function getTenantBySlug(slug: string): CollectiveTenant | undefined {
  return COLLECTIVE_TENANTS.find((tenant) => tenant.slug === slug);
}

export function getFeaturedTenants(): readonly CollectiveTenant[] {
  return COLLECTIVE_TENANTS.filter((tenant) => tenant.featured);
}
